import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IProduct } from './interface';
import {
  getCategoriesProductAsync,
  getCommentsProductAsync,
  getConfigPizzaAsync,
  getEdgesProductAsync,
  getExtrasProductAsync,
  getFamilyProductAsync,
  getLastCodeAsync,
  getPizzasAsync,
  getProductAsync,
  getProductCestsAsync,
  getProductCfopAsync,
  getProductCstOriginsAsync,
  getProductCstReasonsAsync,
  getProductCstsAsync,
  getProductIcmsCustomerTypesAsync,
  getProductInvoiceTypesAsync,
  getProductLocalStockAsync,
  getProductMeasureAsync,
  getProductNcmAsync,
  getProductPurposeAsync,
  getProductStateAsync,
  getProductsWithFiltersAsync,
  getProductTaxProfileAsync,
  getTypesProductAsync,
  getVendorAsync,
  getAllProductsAsync,
} from './services';
import { Category } from './interface/category';

interface InitialState {
  value: ProductState;
}

interface StateForm {
  state: 'error' | 'loading' | 'send' | 'default';
  message?: string;
}

interface ProductState {
  loading: boolean;
  fetchError: string;
  products: any | [];
  flavors: any[] | [];
  edges: any[] | [];
  extras: any[] | [];
  comments: any[] | [];
  product: any;
  price: number | undefined;
  idPdv: string | undefined;
  productsFamily: IProduct[];
  categories: Category[];
  pizzas: IProduct[] | [];
  selectedRows: any[];
  productTypes: any[];
  productResponse: any;
  vendors: any[];
  lastCode: any[];
  measure: any[];
  purpose: any[];
  localStocks: any[];
  taxProfiles: any[];
  invoiceTypes: any[];
  cstOrigins: any[];
  states: any[];
  ncms: any[];
  cests: any[];
  cfops: any[];
  icmsCustomerTypes: any[];
  icmsList: any[];
  ipisList: any[];
  pisList: any[];
  cstReasonsList: any[];
  pizzaConfig: any;
  configPizzaUuid: any;
  allProducts: IProduct[];
}

const initialState: InitialState = {
  value: {
    loading: false,
    fetchError: '',
    flavors: [],
    products: [],
    edges: [],
    extras: [],
    comments: [],
    product: null,
    price: undefined,
    idPdv: undefined,
    productsFamily: [],
    categories: [],
    pizzas: [],
    selectedRows: [],
    productTypes: [],
    productResponse: undefined,
    vendors: [],
    lastCode: [],
    measure: [],
    purpose: [],
    localStocks: [],
    taxProfiles: [],
    states: [],
    invoiceTypes: [],
    cstOrigins: [],
    ncms: [],
    cests: [],
    cfops: [],
    icmsCustomerTypes: [],
    cstReasonsList: [],
    icmsList: [],
    ipisList: [],
    pisList: [],
    pizzaConfig: undefined,
    configPizzaUuid: '',
    allProducts: [],
  } as ProductState,
};

export const product = createSlice({
  name: 'product',
  initialState,
  reducers: {
    setSelectedRows: (state, action: PayloadAction<any[]>) => {
      state.value.selectedRows = action.payload;
    },
    setProductsResponse: (state, action: PayloadAction<any>) => {
      state.value.productResponse = action.payload;
    },
    removeSelectedProducts: (state) => {
      state.value.flavors = state.value.flavors.filter(
        (product) =>
          !state.value.selectedRows.some(
            (row) => row.ItemKeyId === product.ItemKeyId
          )
      );
      state.value.selectedRows = [];
    },
    removeSelectedEdges: (state) => {
      state.value.edges = state.value.edges.filter(
        (product) =>
          !state.value.selectedRows.some(
            (row) => row.ItemKeyId === product.ItemKeyId
          )
      );
      state.value.selectedRows = [];
    },
    removeSelectedExtras: (state) => {
      state.value.extras = state.value.extras.filter(
        (product) =>
          !state.value.selectedRows.some(
            (row) => row.ItemKeyId === product.ItemKeyId
          )
      );
      state.value.selectedRows = [];
    },
    removeSelectedComments: (state) => {
      state.value.comments = state.value.comments.filter(
        (product) =>
          !state.value.selectedRows.some(
            (row) => row.ItemKeyId === product.ItemKeyId
          )
      );
      state.value.selectedRows = [];
    },
    setProduct: (state, action: PayloadAction<any>) => {
      state.value.product = action.payload;
    },
    setProducts: (state, action: PayloadAction<any>) => {
      state.value.products = action.payload;
    },
    setPrice: (state, action: PayloadAction<number | undefined>) => {
      state.value.price = action.payload;
    },
    setIdPdv: (state, action: PayloadAction<string | undefined>) => {
      state.value.idPdv = action.payload;
    },
    setConfigUuid: (state, action: PayloadAction<string>) => {
      state.value.configPizzaUuid = action.payload;
    },
    addProductTable: (state, action: PayloadAction<any>) => {
      const newItems = Array.isArray(action.payload)
        ? action.payload.filter((item: any) => item !== undefined)
        : [action.payload];

      state.value.flavors = [...state.value.flavors, ...newItems];
    },
    addProductTableEdges: (state, action: PayloadAction<any>) => {
      if (Array.isArray(action.payload) && action.payload.length === 0) return;
      state.value.edges = Array.isArray(action.payload)
        ? [...state.value.edges, ...action.payload]
        : [...state.value.edges, action.payload];
    },

    addProductTableExtras: (state, action: PayloadAction<any>) => {
      if (Array.isArray(action.payload) && action.payload.length === 0) return;
      state.value.extras = Array.isArray(action.payload)
        ? [...state.value.extras, ...action.payload]
        : [...state.value.extras, action.payload];
    },

    addProductTableComments: (state, action: PayloadAction<any>) => {
      if (Array.isArray(action.payload) && action.payload.length === 0) return;
      state.value.comments = Array.isArray(action.payload)
        ? [...state.value.comments, ...action.payload]
        : [...state.value.comments, action.payload];
    },

    handleCloseModal: (state) => {
      state.value.flavors = [];
      state.value.edges = [];
      state.value.extras = [];
      state.value.comments = [];
      state.value.price = undefined;
      state.value.product = [];
      //state.value.pizzaConfig = undefined;
      state.value.configPizzaUuid = undefined;
    },
  },
  extraReducers: (builder: any) => {
    builder.addCase(getConfigPizzaAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getConfigPizzaAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any>) => {
        state.value.loading = false;
        state.value.pizzaConfig = action.payload;
        state.value.flavors = [
          ...state.value.flavors,
          ...action.payload?.items?.sabor,
        ];
        state.value.edges = [
          ...state.value.edges,
          ...action.payload?.items?.borda,
        ];
        state.value.extras = [
          ...state.value.extras,
          ...action.payload?.items?.extra,
        ];
        state.value.comments = [
          ...state.value.comments,
          ...action.payload?.items?.comentario,
        ];
      }
    );
    builder.addCase(
      getConfigPizzaAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getFamilyProductAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getFamilyProductAsync.fulfilled,
      (state: InitialState, action: PayloadAction<IProduct[]>) => {
        state.value.loading = false;
        state.value.flavors = [...state.value.flavors, ...action.payload];
      }
    );
    builder.addCase(
      getFamilyProductAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getEdgesProductAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getEdgesProductAsync.fulfilled,
      (state: InitialState, action: PayloadAction<IProduct[]>) => {
        state.value.loading = false;
        state.value.edges = [...state.value.edges, ...action.payload];
      }
    );
    builder.addCase(
      getEdgesProductAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getExtrasProductAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getExtrasProductAsync.fulfilled,
      (state: InitialState, action: PayloadAction<IProduct[]>) => {
        state.value.loading = false;
        state.value.extras = [...state.value.extras, ...action.payload];
      }
    );
    builder.addCase(
      getExtrasProductAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getCommentsProductAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getCommentsProductAsync.fulfilled,
      (state: InitialState, action: PayloadAction<IProduct[]>) => {
        state.value.loading = false;
        state.value.comments = [...state.value.comments, ...action.payload];
      }
    );
    builder.addCase(
      getCommentsProductAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getProductAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getProductAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any>) => {
        state.value.loading = false;
        state.value.products = action.payload;
      }
    );
    builder.addCase(
      getProductAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );
    builder.addCase(
      getCategoriesProductAsync.pending,
      (state: InitialState) => {
        state.value.loading = true;
      }
    );
    builder.addCase(
      getCategoriesProductAsync.fulfilled,
      (state: InitialState, action: PayloadAction<Category[]>) => {
        state.value.loading = false;
        state.value.categories = action.payload;
      }
    );
    builder.addCase(
      getCategoriesProductAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getPizzasAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getPizzasAsync.fulfilled,
      (state: InitialState, action: PayloadAction<IProduct[]>) => {
        state.value.loading = false;
        state.value.pizzas = action.payload;
      }
    );
    builder.addCase(
      getPizzasAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getTypesProductAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getTypesProductAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.productTypes = action.payload;
      }
    );
    builder.addCase(
      getTypesProductAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(
      getProductsWithFiltersAsync.pending,
      (state: InitialState) => {
        state.value.loading = true;
      }
    );
    builder.addCase(
      getProductsWithFiltersAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.productResponse = action.payload;
      }
    );
    builder.addCase(
      getProductsWithFiltersAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getVendorAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getVendorAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.vendors = action.payload;
      }
    );
    builder.addCase(
      getVendorAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getLastCodeAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getLastCodeAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.lastCode = action.payload;
      }
    );
    builder.addCase(
      getLastCodeAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getProductPurposeAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getProductPurposeAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.purpose = action.payload;
      }
    );
    builder.addCase(
      getProductPurposeAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getProductMeasureAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getProductMeasureAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.measure = action.payload;
      }
    );
    builder.addCase(
      getProductMeasureAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(
      getProductLocalStockAsync.pending,
      (state: InitialState) => {
        state.value.loading = true;
      }
    );
    builder.addCase(
      getProductLocalStockAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.localStocks = action.payload;
      }
    );
    builder.addCase(
      getProductLocalStockAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(
      getProductTaxProfileAsync.pending,
      (state: InitialState) => {
        state.value.loading = true;
      }
    );
    builder.addCase(
      getProductTaxProfileAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.taxProfiles = action.payload;
      }
    );
    builder.addCase(
      getProductTaxProfileAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getProductStateAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getProductStateAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.states = action.payload;
      }
    );
    builder.addCase(
      getProductStateAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(
      getProductInvoiceTypesAsync.pending,
      (state: InitialState) => {
        state.value.loading = true;
      }
    );
    builder.addCase(
      getProductInvoiceTypesAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.invoiceTypes = action.payload;
      }
    );
    builder.addCase(
      getProductInvoiceTypesAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(
      getProductCstOriginsAsync.pending,
      (state: InitialState) => {
        state.value.loading = true;
      }
    );
    builder.addCase(
      getProductCstOriginsAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.cstOrigins = action.payload;
      }
    );
    builder.addCase(
      getProductCstOriginsAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getProductNcmAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getProductNcmAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.ncms = action.payload;
      }
    );
    builder.addCase(
      getProductNcmAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getProductCestsAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getProductCestsAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.cests = action.payload;
      }
    );
    builder.addCase(
      getProductCestsAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getProductCfopAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getProductCfopAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.cfops = action.payload;
      }
    );
    builder.addCase(
      getProductCfopAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(
      getProductIcmsCustomerTypesAsync.pending,
      (state: InitialState) => {
        state.value.loading = true;
      }
    );
    builder.addCase(
      getProductIcmsCustomerTypesAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.icmsCustomerTypes = action.payload;
      }
    );
    builder.addCase(
      getProductIcmsCustomerTypesAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(
      getProductCstReasonsAsync.pending,
      (state: InitialState) => {
        state.value.loading = true;
      }
    );
    builder.addCase(
      getProductCstReasonsAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.cstReasonsList = action.payload;
      }
    );
    builder.addCase(
      getProductCstReasonsAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getProductCstsAsync.pending, (state: InitialState) => {
      state.value.loading = true;
    });
    builder.addCase(
      getProductCstsAsync.fulfilled,
      (state: InitialState, action: PayloadAction<any[]>) => {
        state.value.loading = false;
        state.value.icmsList = action.payload.filter(
          (item: any) => item.tax_type === 'ICMS'
        );
        state.value.ipisList = action.payload.filter(
          (item: any) => item.tax_type === 'IPI'
        );
        state.value.pisList = action.payload.filter(
          (item: any) => item.tax_type === 'PIS/COFINS'
        );
      }
    );
    builder.addCase(
      getProductCstsAsync.rejected,
      (state: InitialState, action: { payload: string }) => {
        state.value.loading = false;
        state.value.fetchError = action.payload;
      }
    );

    builder.addCase(getAllProductsAsync.pending, (state: InitialState) => {
  state.value.loading = true;
});
builder.addCase(
  getAllProductsAsync.fulfilled,
  (state: InitialState, action: PayloadAction<IProduct[]>) => {
    state.value.loading = false;
    state.value.allProducts = action.payload;
  }
);
builder.addCase(
  getAllProductsAsync.rejected,
  (state: InitialState, action: { payload: string }) => {
    state.value.loading = false;
    state.value.fetchError = action.payload;
  }
);

  },
});

export const {
  setSelectedRows,
  removeSelectedProducts,
  setProduct,
  addProductTable,
  setPrice,
  removeSelectedEdges,
  removeSelectedExtras,
  addProductTableEdges,
  addProductTableExtras,
  handleCloseModal,
  setIdPdv,
  setProducts,
  addProductTableComments,
  removeSelectedComments,
  setConfigUuid,
  setProductsResponse,
} = product.actions;

export default product.reducer;
